﻿import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Heading, Text, Input, Button, LoadingSpinner, SortableTableHeader, Editor, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { modulesService } from '../services/modules'
import { dashcorretorService } from '../services/dashcorretor'
import { serverInfoService } from '../services/serverInfo'
import { userLogsService } from '../services/userLogs'
import { HistoricoAcesso, Broker, DashCorretorParameter, DashCorretorTopAcessoEntry, DashCorretorClassificationRef } from '../types/dashcorretor'
import { ClassificationWithTrend } from '../components/ClassificationTrendIndicator'
import { buildClassificationColorMap, buildClassificationRankMap, normalizeClassificationKey } from '../utils/classificationTrend'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import {
  getDateInSPTimezone,
  enumerateDateRangeYmd,
  getStartDateForLastNDaysInclusive,
} from '../services/superapp'

const DASHBOARD_CORRETOR_PROD_ORIGIN = 'https://dashboardcorretor.portalpasi.com.br'
const HISTORICO_CHART_MAX_DAYS = 90
const DASHBOARD_CORRETOR_HOMOLOG_ORIGIN = 'http://10.60.60.115:3071'

const DashboardCorretor: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'historico' | 'corretores' | 'parametros'>('historico')
  const [historicoView, setHistoricoView] = useState<'lista' | 'graficos'>('lista')
  const [historicoCorretorCodeFilter, setHistoricoCorretorCodeFilter] = useState('')
  const [corretorQuery, setCorretorQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [moduleTitle, setModuleTitle] = useState<string>('Dashboard Corretor')
  const [moduleDescription, setModuleDescription] = useState<string>('Visão de acessos por corretor')

  // Estados para aba de corretores (tabela brokers)
  const [loadingBrokers, setLoadingBrokers] = useState<boolean>(false)
  const [brokers, setBrokers] = useState<Broker[]>([])
  const [classificationRefs, setClassificationRefs] = useState<DashCorretorClassificationRef[]>([])
  const [corretorCodeFilter, setCorretorCodeFilter] = useState<string>('')
  const [corretorNameFilter, setCorretorNameFilter] = useState<string>('')
  const [corretorClassificationFilter, setCorretorClassificationFilter] = useState<string>('')
  const [corretorUfFilter, setCorretorUfFilter] = useState<string>('')
  const [corretorCityFilter, setCorretorCityFilter] = useState<string>('')
  const [appliedCorretorFilters, setAppliedCorretorFilters] = useState({
    code: '',
    name: '',
    classification: '',
    uf: '',
    city: '',
  })
  const [hasSearchedCorretores, setHasSearchedCorretores] = useState(false)
  const [currentPageBrokers, setCurrentPageBrokers] = useState(1)
  const [itemsPerPageBrokers] = useState(20)
  const [acessandoBrokerId, setAcessandoBrokerId] = useState<string | null>(null)
  const [homologandoBrokerId, setHomologandoBrokerId] = useState<string | null>(null)

  // Estados para aba de histórico
  const [loadingHistorico, setLoadingHistorico] = useState<boolean>(true)
  const [historicoAcessos, setHistoricoAcessos] = useState<HistoricoAcesso[]>([])
  const [historicoAcessosPrimeiroAcesso, setHistoricoAcessosPrimeiroAcesso] = useState<HistoricoAcesso[]>([])
  const [totalHistorico, setTotalHistorico] = useState<number>(0)
  const [acessosHoje, setAcessosHoje] = useState<number>(0)
  const [corretoresComAcesso, setCorretoresComAcesso] = useState<number>(0)
  const [topCorretoresAcesso, setTopCorretoresAcesso] = useState<DashCorretorTopAcessoEntry[]>([])

  // Estados para ordenação
  const [sortBy, setSortBy] = useState<string>('access_timestamp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Navegação horizontal da tabela
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTableBy = (dx: number) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: dx, behavior: 'smooth' })
    }
  }
  const handleTableWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Converte scroll vertical do mouse em horizontal
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollBy({ left: e.deltaY, behavior: 'auto' })
      }
    }
  }

  // Estados para aba de parâmetros
  const [parameters, setParameters] = useState<DashCorretorParameter[]>([])
  const [loadingParameters, setLoadingParameters] = useState<boolean>(false)
  const [editingParameter, setEditingParameter] = useState<DashCorretorParameter | null>(null)
  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false)
  const [parameterEditValue, setParameterEditValue] = useState<string>('')
  const [savingParameter, setSavingParameter] = useState(false)
  const [parameterEditMode, setParameterEditMode] = useState<'simple' | 'html'>('simple')

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('portais_dashboard_corretor')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Buscar histórico de acessos
  const loadHistoricoAcessos = useCallback(async () => {
    if (activeTab !== 'historico') return

    setLoadingHistorico(true)
    try {
      // Buscar dados em paralelo
      const filtrosComuns = {
        corretorCodeQuery: historicoCorretorCodeFilter || undefined,
        corretorQuery: corretorQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }

      const [historico, historicoPrimeiroAcesso, total, hoje, brokerStats] = await Promise.all([
        dashcorretorService.getHistoricoAcessos({
          ...filtrosComuns,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        }),
        dashcorretorService.getHistoricoAcessos(filtrosComuns),
        dashcorretorService.getTotalAcessos(),
        dashcorretorService.getAcessosHoje(),
        dashcorretorService.getBrokerAccessStats(3),
      ])

      setHistoricoAcessos(historico)
      setHistoricoAcessosPrimeiroAcesso(historicoPrimeiroAcesso)
      setTotalHistorico(total)
      setAcessosHoje(hoje)
      setCorretoresComAcesso(brokerStats.distinctCorretores)
      setTopCorretoresAcesso(brokerStats.topCorretores)
    } catch (error) {
      console.error('Erro ao carregar histórico de acessos:', error)
      // Em caso de erro, definir valores padrão
      setHistoricoAcessos([])
      setHistoricoAcessosPrimeiroAcesso([])
      setTotalHistorico(0)
      setAcessosHoje(0)
      setCorretoresComAcesso(0)
      setTopCorretoresAcesso([])
    } finally {
      setLoadingHistorico(false)
    }
  }, [activeTab, historicoCorretorCodeFilter, corretorQuery, statusFilter, dateFrom, dateTo])

  useEffect(() => {
    loadHistoricoAcessos()
  }, [loadHistoricoAcessos])

  // Função para alternar ordenação
  const toggleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }, [sortBy, sortOrder])

  // Aplicar ordenação e filtros
  const filtrados = useMemo(() => {
    let resultado = [...historicoAcessos]

    // Aplicar ordenação
    resultado.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortBy) {
        case 'corretor':
          aValue = a.corretor || ''
          bValue = b.corretor || ''
          break
        case 'id_scgp':
          aValue = a.id_scgp != null ? Number(a.id_scgp) : Number.POSITIVE_INFINITY
          bValue = b.id_scgp != null ? Number(b.id_scgp) : Number.POSITIVE_INFINITY
          break
        case 'access_timestamp':
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
          break
        case 'route':
          aValue = a.route || ''
          bValue = b.route || ''
          break
        case 'status':
          aValue = a.status || ''
          bValue = b.status || ''
          break
        case 'error_message':
          aValue = a.error_message || ''
          bValue = b.error_message || ''
          break
        case 'user_agent':
          aValue = a.user_agent || ''
          bValue = b.user_agent || ''
          break
        default:
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
      }

      // Comparação
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return resultado
  }, [historicoAcessos, sortBy, sortOrder])

  // Calcular dados da paginação
  const totalPages = Math.ceil(filtrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const filtradosPaginados = filtrados.slice(startIndex, endIndex)

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [historicoCorretorCodeFilter, corretorQuery, statusFilter, dateFrom, dateTo])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  const formatDateShort = (dateString: string) => {
    const [y, m, d] = dateString.split('-').map(Number)
    if (!y || !m || !d) return dateString
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
  }

  const formatChartDateTick = (dateString: string) => {
    const [, m, d] = dateString.split('-')
    return `${d}/${m}`
  }

  const buildFilledDailySeries = useCallback(
    (counts: Map<string, number>) => {
      const today = getDateInSPTimezone(new Date())
      const sortedDates = Array.from(counts.keys()).sort()

      if (sortedDates.length === 0 && !dateFrom && !dateTo) {
        return []
      }

      let startDate = dateFrom ? dateFrom.split('T')[0] : sortedDates[0] ?? today
      let endDate = dateTo ? dateTo.split('T')[0] : sortedDates[sortedDates.length - 1] ?? today

      if (!dateFrom && !dateTo && sortedDates.length > 0) {
        const [y1, m1, d1] = sortedDates[0].split('-').map(Number)
        const [y2, m2, d2] = today.split('-').map(Number)
        const first = new Date(y1, m1 - 1, d1)
        const last = new Date(y2, m2 - 1, d2)
        const spanDays = Math.floor((last.getTime() - first.getTime()) / 86_400_000) + 1
        if (spanDays > HISTORICO_CHART_MAX_DAYS) {
          startDate = getStartDateForLastNDaysInclusive(HISTORICO_CHART_MAX_DAYS)
          endDate = today
        }
      }

      if (startDate > endDate) {
        return []
      }

      return enumerateDateRangeYmd(startDate, endDate).map((date) => ({
        date,
        total: counts.get(date) ?? 0,
      }))
    },
    [dateFrom, dateTo]
  )

  const acessosPorData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const log of historicoAcessos) {
      if (!log.access_timestamp) continue
      const date = getDateInSPTimezone(new Date(log.access_timestamp))
      counts.set(date, (counts.get(date) ?? 0) + 1)
    }
    return buildFilledDailySeries(counts)
  }, [historicoAcessos, buildFilledDailySeries])

  const corretoresPrimeiroAcessoPorData = useMemo(() => {
    const primeiroAcessoPorBroker = new Map<string, string>()
    for (const log of historicoAcessosPrimeiroAcesso) {
      if (!log.broker_id || !log.access_timestamp) continue
      const existing = primeiroAcessoPorBroker.get(log.broker_id)
      if (!existing || log.access_timestamp < existing) {
        primeiroAcessoPorBroker.set(log.broker_id, log.access_timestamp)
      }
    }

    const counts = new Map<string, number>()
    for (const ts of primeiroAcessoPorBroker.values()) {
      const date = getDateInSPTimezone(new Date(ts))
      counts.set(date, (counts.get(date) ?? 0) + 1)
    }
    return buildFilledDailySeries(counts)
  }, [historicoAcessosPrimeiroAcesso, buildFilledDailySeries])

  const truncateCorretorName = (name: string, maxChars = 60) => {
    if (!name) return ''
    return name.length > maxChars ? `${name.slice(0, maxChars)}...` : name
  }

  // Carregar corretores (tabela brokers) quando abrir a aba
  const loadBrokers = useCallback(async () => {
    setLoadingBrokers(true)
    setBrokers([]) // Limpar dados anteriores
    try {
      console.log('Carregando corretores...')
      const [data, refs] = await Promise.all([
        dashcorretorService.listBrokers(),
        dashcorretorService.listClassificationReferences(),
      ])
      console.log(`Corretores carregados: ${data.length} registros`)
      setBrokers(data)
      setClassificationRefs(refs)
      if (data.length === 0) {
        console.warn('Nenhum corretor encontrado na tabela brokers')
      }
    } catch (error) {
      console.error('Erro ao carregar corretores:', error)
      if (error instanceof Error) {
        console.error('Mensagem de erro:', error.message)
        console.error('Stack:', error.stack)
      }
      // Manter array vazio em caso de erro
      setBrokers([])
      setClassificationRefs([])
    } finally {
      setLoadingBrokers(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'corretores') {
      setCurrentPageBrokers(1)
    }
  }, [activeTab])

  useEffect(() => {
    if (classificationRefs.length > 0) return
    dashcorretorService
      .listClassificationReferences()
      .then(setClassificationRefs)
      .catch((error) => console.error('Erro ao carregar referências de categoria:', error))
  }, [classificationRefs.length])

  // Carregar parâmetros do dashboard corretor
  const loadParameters = useCallback(async () => {
    if (activeTab !== 'parametros') return

    setLoadingParameters(true)
    try {
      const data = await dashcorretorService.listParameters()
      setParameters(data)
    } catch (error) {
      console.error('Erro ao carregar parâmetros do dashboard corretor:', error)
      setParameters([])
    } finally {
      setLoadingParameters(false)
    }
  }, [activeTab])

  useEffect(() => {
    loadParameters()
  }, [loadParameters])

  const classificationRankMap = useMemo(
    () => buildClassificationRankMap(classificationRefs),
    [classificationRefs]
  )

  const classificationColorMap = useMemo(
    () => buildClassificationColorMap(classificationRefs),
    [classificationRefs]
  )

  const classificationsBrokers = useMemo(() => {
    if (classificationRefs.length > 0) {
      return classificationRefs.map((r) => r.name).sort((a, b) => a.localeCompare(b, 'pt-BR'))
    }
    const set = new Set<string>()
    brokers.forEach((b) => {
      if (b.classification?.trim()) set.add(b.classification.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [classificationRefs, brokers])

  const ufsBrokers = useMemo(() => {
    const set = new Set<string>()
    brokers.forEach((b) => {
      if (b.state?.trim()) set.add(b.state.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [brokers])

  /** Cidades disponíveis no dropdown: todas se UF vazio; senão só da UF selecionada */
  const citiesBrokersOptions = useMemo(() => {
    const source = corretorUfFilter
      ? brokers.filter((b) => (b.state || '').trim() === corretorUfFilter)
      : brokers
    const set = new Set<string>()
    source.forEach((b) => {
      if (b.city?.trim()) set.add(b.city.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [brokers, corretorUfFilter])

  useEffect(() => {
    if (corretorCityFilter && !citiesBrokersOptions.includes(corretorCityFilter)) {
      setCorretorCityFilter('')
    }
  }, [corretorUfFilter, citiesBrokersOptions, corretorCityFilter])

  const filteredBrokers = useMemo(() => {
    let result = brokers
    const code = appliedCorretorFilters.code.trim()
    if (code) {
      result = result.filter((b) => String(b.id_scgp ?? '').includes(code))
    }
    const name = appliedCorretorFilters.name.trim().toLowerCase()
    if (name) {
      result = result.filter((b) => (b.name || '').toLowerCase().includes(name))
    }
    if (appliedCorretorFilters.classification) {
      const filterKey = normalizeClassificationKey(appliedCorretorFilters.classification)
      result = result.filter((b) => normalizeClassificationKey(b.classification) === filterKey)
    }
    if (appliedCorretorFilters.uf) {
      result = result.filter((b) => (b.state || '').trim() === appliedCorretorFilters.uf)
    }
    if (appliedCorretorFilters.city) {
      result = result.filter((b) => (b.city || '').trim() === appliedCorretorFilters.city)
    }
    return result
  }, [brokers, appliedCorretorFilters])

  // Calcular dados da paginação para corretores
  const totalPagesBrokers = Math.ceil(filteredBrokers.length / itemsPerPageBrokers)
  const startIndexBrokers = (currentPageBrokers - 1) * itemsPerPageBrokers
  const endIndexBrokers = startIndexBrokers + itemsPerPageBrokers
  const filteredBrokersPaginados = filteredBrokers.slice(startIndexBrokers, endIndexBrokers)

  // Handlers de paginação para corretores
  const handlePageChangeBrokers = useCallback((page: number) => {
    setCurrentPageBrokers(page)
  }, [])

  const handlePreviousPageBrokers = useCallback(() => {
    if (currentPageBrokers > 1) {
      setCurrentPageBrokers(currentPageBrokers - 1)
    }
  }, [currentPageBrokers])

  const handleNextPageBrokers = useCallback(() => {
    if (currentPageBrokers < totalPagesBrokers) {
      setCurrentPageBrokers(currentPageBrokers + 1)
    }
  }, [currentPageBrokers, totalPagesBrokers])

  const hasCorretoresFiltersActive = useMemo(
    () =>
      !!(
        corretorCodeFilter.trim() ||
        corretorNameFilter.trim() ||
        corretorClassificationFilter ||
        corretorUfFilter ||
        corretorCityFilter
      ),
    [
      corretorCodeFilter,
      corretorNameFilter,
      corretorClassificationFilter,
      corretorUfFilter,
      corretorCityFilter,
    ]
  )

  const applyCorretoresFilters = () => {
    if (!hasCorretoresFiltersActive) return

    setAppliedCorretorFilters({
      code: corretorCodeFilter,
      name: corretorNameFilter,
      classification: corretorClassificationFilter,
      uf: corretorUfFilter,
      city: corretorCityFilter,
    })
    setCurrentPageBrokers(1)
    setHasSearchedCorretores(true)
    void loadBrokers()
  }

  const clearCorretoresFilters = () => {
    setCorretorCodeFilter('')
    setCorretorNameFilter('')
    setCorretorClassificationFilter('')
    setCorretorUfFilter('')
    setCorretorCityFilter('')
    setAppliedCorretorFilters({
      code: '',
      name: '',
      classification: '',
      uf: '',
      city: '',
    })
    setCurrentPageBrokers(1)
    setHasSearchedCorretores(false)
    setBrokers([])
  }

  const fetchDashcorretorLink = async (broker: Broker): Promise<string> => {
    const codigocorretor = broker.id_scgp
    if (codigocorretor == null) {
      throw new Error('Este corretor não possui código SCGP (id_scgp).')
    }
    const result = await serverInfoService.getDashcorretorLink(codigocorretor)
    if (result.success && result.data) {
      return result.data
    }
    throw new Error(result.error || 'Erro ao gerar link')
  }

  const adaptLinkToHomologacao = (link: string): string => {
    if (!link.startsWith(DASHBOARD_CORRETOR_PROD_ORIGIN)) {
      throw new Error('Link do dashboard em formato inesperado para homologação')
    }
    return `${DASHBOARD_CORRETOR_HOMOLOG_ORIGIN}${link.slice(DASHBOARD_CORRETOR_PROD_ORIGIN.length)}`
  }

  const handleAcessar = async (broker: Broker) => {
    setAcessandoBrokerId(broker.id)
    try {
      const link = await fetchDashcorretorLink(broker)
      window.open(link, '_blank')
      try {
        await userLogsService.logUserAction({
          action: 'view',
          entityType: 'other',
          entityId: broker.id,
          description: `Acessou dashboard do corretor ${broker.name || broker.id}`,
          metadata: {
            broker_id: broker.id,
            broker_name: broker.name,
            id_scgp: broker.id_scgp,
            link,
            origem: 'portais/dashboard_corretor',
          },
        })
      } catch (logError) {
        console.warn('Erro ao registrar log de acesso ao dashboard corretor:', logError)
      }
    } catch (error) {
      console.error('Erro ao acessar dashboard do corretor:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    } finally {
      setAcessandoBrokerId(null)
    }
  }

  const handleHomologacao = async (broker: Broker) => {
    setHomologandoBrokerId(broker.id)
    try {
      const link = await fetchDashcorretorLink(broker)
      const homologLink = adaptLinkToHomologacao(link)
      window.open(homologLink, '_blank')
      try {
        await userLogsService.logUserAction({
          action: 'view',
          entityType: 'other',
          entityId: broker.id,
          description: `Acessou homologação do dashboard do corretor ${broker.name || broker.id}`,
          metadata: {
            broker_id: broker.id,
            broker_name: broker.name,
            id_scgp: broker.id_scgp,
            link: homologLink,
            link_original: link,
            ambiente: 'homologacao',
            origem: 'portais/dashboard_corretor',
          },
        })
      } catch (logError) {
        console.warn('Erro ao registrar log de acesso à homologação do dashboard corretor:', logError)
      }
    } catch (error) {
      console.error('Erro ao acessar homologação do dashboard do corretor:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    } finally {
      setHomologandoBrokerId(null)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName="portais_dashboard_corretor"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src="/Logo_Dashboard_Campanhas-03.png"
                  alt="Dashboard Campanhas"
                  className="h-14 w-auto flex-shrink-0 object-contain select-none"
                />
                <div className="min-w-0">
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
                  <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
                </div>
              </div>
            </div>

            {/* Navegação das Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('historico')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'historico'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Histórico de Acessos
                </button>
                <button
                  onClick={() => setActiveTab('corretores')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'corretores'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Corretores
                </button>
                <button
                  onClick={() => setActiveTab('parametros')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'parametros'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Parâmetros
                </button>
              </nav>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'historico' && (
              <>
            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Código do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Código SCGP..."
                      value={historicoCorretorCodeFilter}
                      onChange={(e) => setHistoricoCorretorCodeFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Corretor</Text>
                    <Input
                      type="text"
                      placeholder="Digite o nome do corretor..."
                      value={corretorQuery}
                      onChange={(e) => setCorretorQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                    <select
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'failed')}
                    >
                      <option value="all">Todos</option>
                      <option value="success">Sucesso</option>
                      <option value="failed">Falha</option>
                    </select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                    <Input
                      type="date"
                      value={dateFrom ? dateFrom.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        setDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                    <Input
                      type="date"
                      value={dateTo ? dateTo.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        setDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full md:w-auto"
                      onClick={() => {
                        setHistoricoCorretorCodeFilter('')
                        setCorretorQuery('')
                        setStatusFilter('all')
                        setDateFrom('')
                        setDateTo('')
                      }}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-abas: Lista / Gráficos */}
            <div className="px-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8" aria-label="Visualização do histórico">
                <button
                  type="button"
                  onClick={() => setHistoricoView('lista')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    historicoView === 'lista'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Lista
                </button>
                <button
                  type="button"
                  onClick={() => setHistoricoView('graficos')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    historicoView === 'graficos'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Gráficos
                </button>
              </nav>
            </div>

            {historicoView === 'lista' && (
              <>
            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">Histórico de Acessos</Heading>
                  {!loadingHistorico && filtrados.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollTableBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollTableBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto">
                  {loadingHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div ref={tableScrollRef} className="overflow-x-auto" onWheel={handleTableWheel}>
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="id_scgp"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Código
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="corretor" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="access_timestamp" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data/Hora Acesso
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="route" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Rota
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="status" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Status
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="error_message" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Mensagem de Erro
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="user_agent" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              User Agent
                            </SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filtradosPaginados.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">Nenhum dado disponível.</Text>
                              </td>
                            </tr>
                          ) : (
                            filtradosPaginados.map((r, index) => (
                              <tr key={`${r.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left whitespace-nowrap">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono">
                                    {r.id_scgp != null ? String(r.id_scgp) : '—'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <div className="flex flex-col">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">{r.corretor || '-'}</Text>
                                    {r.cnpj && (
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">{r.cnpj}</Text>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(r.access_timestamp)}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all max-w-xs">{r.route || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    r.status === 'success' 
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {r.status === 'success' ? 'Sucesso' : 'Falha'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.error_message || ''}>
                                    {r.error_message ? (r.error_message.length > 50 ? `${r.error_message.substring(0, 50)}...` : r.error_message) : '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.user_agent || ''}>
                                    {r.user_agent ? (r.user_agent.length > 50 ? `${r.user_agent.substring(0, 50)}...` : r.user_agent) : '-'}
                                  </Text>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {totalPages > 1 && !loadingHistorico && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, filtrados.length)} de {filtrados.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number
                            if (totalPages <= 5) {
                              pageNumber = i + 1
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i
                            } else {
                              pageNumber = currentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {historicoView === 'graficos' && (
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{totalHistorico}</Text>
                      <Text size="sm" className="text-white">Acessos - Histórico</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{acessosHoje}</Text>
                      <Text size="sm" className="text-white">Acessos Hoje</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{corretoresComAcesso}</Text>
                      <Text size="sm" className="text-white">Corretores que já acessaram</Text>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-600 to-slate-700 shadow-lg rounded-lg p-6 text-white">
                  <Text size="sm" className="text-white/90 font-semibold mb-3 block text-center md:text-left">
                    Top 3 corretores com mais acessos
                  </Text>
                  {loadingHistorico ? (
                    <div className="flex justify-center py-2">
                      <LoadingSpinner />
                    </div>
                  ) : topCorretoresAcesso.length === 0 ? (
                    <Text size="sm" className="text-white/90 text-center">
                      Nenhum acesso vinculado a corretor no histórico.
                    </Text>
                  ) : (
                    <ol className="space-y-3 list-decimal list-inside">
                      {topCorretoresAcesso.map((c) => (
                        <li key={c.broker_id} className="text-sm marker:font-bold">
                          <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="font-medium">{truncateCorretorName(c.nome, 48)}</span>
                            {c.id_scgp != null && (
                              <span className="text-white/80 text-xs">SCGP {c.id_scgp}</span>
                            )}
                            <span className="font-bold tabular-nums">{c.acessos} acessos</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Acessos por data
                  </Heading>
                  {!dateFrom && !dateTo && historicoAcessos.length > 0 && (
                    <Text size="sm" color="muted" className="mb-4 block">
                      {`Exibindo últimos ${HISTORICO_CHART_MAX_DAYS} dias quando o histórico é longo`}
                    </Text>
                  )}

                  {loadingHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : acessosPorData.length === 0 || acessosPorData.every((d) => d.total === 0) ? (
                    <div className="flex items-center justify-center py-12">
                      <Text size="sm" color="muted">
                        Nenhum acesso no período selecionado.
                      </Text>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={acessosPorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="date"
                            stroke="#6b7280"
                            tickFormatter={formatChartDateTick}
                            interval="preserveStartEnd"
                            minTickGap={24}
                          />
                          <YAxis stroke="#6b7280" allowDecimals={false} />
                          <Tooltip
                            formatter={(value) => [`${Number(value).toLocaleString('pt-BR')} acessos`, 'Total']}
                            labelFormatter={(label) => `Data: ${formatDateShort(String(label))}`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#008B95"
                            strokeWidth={2}
                            dot={{ fill: '#008B95', r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Corretores por data de primeiro acesso
                  </Heading>
                  {!dateFrom && !dateTo && historicoAcessos.length > 0 && (
                    <Text size="sm" color="muted" className="mb-4 block">
                      {`Exibindo últimos ${HISTORICO_CHART_MAX_DAYS} dias quando o histórico é longo`}
                    </Text>
                  )}

                  {loadingHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : corretoresPrimeiroAcessoPorData.length === 0 ||
                    corretoresPrimeiroAcessoPorData.every((d) => d.total === 0) ? (
                    <div className="flex items-center justify-center py-12">
                      <Text size="sm" color="muted">
                        Nenhum corretor com primeiro acesso no período selecionado.
                      </Text>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={corretoresPrimeiroAcessoPorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="date"
                            stroke="#6b7280"
                            tickFormatter={formatChartDateTick}
                            interval="preserveStartEnd"
                            minTickGap={24}
                          />
                          <YAxis stroke="#6b7280" allowDecimals={false} />
                          <Tooltip
                            formatter={(value) => [
                              `${Number(value).toLocaleString('pt-BR')} corretores`,
                              'Primeiro acesso',
                            ]}
                            labelFormatter={(label) => `Data: ${formatDateShort(String(label))}`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#7c3aed"
                            strokeWidth={2}
                            dot={{ fill: '#7c3aed', r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}
              </>
            )}

            {activeTab === 'corretores' && (
              <>
            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Código do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Código SCGP..."
                      value={corretorCodeFilter}
                      onChange={(e) => setCorretorCodeFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Nome do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Nome..."
                      value={corretorNameFilter}
                      onChange={(e) => setCorretorNameFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Categoria</Text>
                    <Select
                      value={corretorClassificationFilter}
                      onChange={(e) => setCorretorClassificationFilter(e.target.value)}
                    >
                      <option value="">Todas</option>
                      {classificationsBrokers.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">UF</Text>
                    <Select
                      value={corretorUfFilter}
                      onChange={(e) => setCorretorUfFilter(e.target.value)}
                    >
                      <option value="">Todas</option>
                      {ufsBrokers.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Cidade</Text>
                    <Select
                      value={corretorCityFilter}
                      onChange={(e) => setCorretorCityFilter(e.target.value)}
                    >
                      <option value="">Todas</option>
                      {citiesBrokersOptions.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="solid"
                    size="sm"
                    onClick={applyCorretoresFilters}
                    className="mr-2"
                    disabled={loadingBrokers || !hasCorretoresFiltersActive}
                    title={!hasCorretoresFiltersActive ? 'Selecione ao menos um filtro para buscar' : undefined}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearCorretoresFilters}
                    disabled={loadingBrokers || (!hasSearchedCorretores && !hasCorretoresFiltersActive)}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Corretores
                    {hasSearchedCorretores && !loadingBrokers && (
                      <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                        ({filteredBrokers.length} {filteredBrokers.length === 1 ? 'corretor' : 'corretores'})
                      </span>
                    )}
                  </Heading>
                </div>
                <div className="flex-1 overflow-auto">
                  {!hasSearchedCorretores ? (
                    <div className="px-4 py-8 text-center">
                      <Text size="sm" color="muted">
                        Selecione ao menos um filtro e clique em Buscar para carregar os registros.
                      </Text>
                    </div>
                  ) : loadingBrokers ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Código SCGP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Categoria</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Estado/Cidade</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredBrokers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">
                                  Nenhum corretor encontrado com os filtros aplicados.
                                </Text>
                              </td>
                            </tr>
                          ) : (
                            filteredBrokersPaginados.map((b) => (
                              <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">{b.name || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono">{b.id_scgp ?? '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                    <ClassificationWithTrend
                                      classification={b.classification}
                                      classificationPrevious={b.classification_previous}
                                      rankMap={classificationRankMap}
                                      colorMap={classificationColorMap}
                                      emptyLabel="-"
                                    />
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                    {[b.state, b.city].filter(Boolean).join(' / ') || '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-right">
                                  {b.id_scgp != null ? (
                                    <div className="inline-flex items-center justify-end gap-2 flex-nowrap">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleAcessar(b)}
                                        disabled={acessandoBrokerId === b.id || homologandoBrokerId === b.id}
                                      >
                                        {acessandoBrokerId === b.id ? (
                                          <span className="inline-flex items-center gap-1">
                                            <LoadingSpinner size="sm" />
                                            Gerando...
                                          </span>
                                        ) : (
                                          'Acessar'
                                        )}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleHomologacao(b)}
                                        disabled={acessandoBrokerId === b.id || homologandoBrokerId === b.id}
                                      >
                                        {homologandoBrokerId === b.id ? (
                                          <span className="inline-flex items-center gap-1">
                                            <LoadingSpinner size="sm" />
                                            Gerando...
                                          </span>
                                        ) : (
                                          'Homologação'
                                        )}
                                      </Button>
                                    </div>
                                  ) : (
                                    <Text size="xs" color="muted">Sem código SCGP</Text>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
  {hasSearchedCorretores && totalPagesBrokers > 1 && !loadingBrokers && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndexBrokers + 1} a {Math.min(endIndexBrokers, filteredBrokers.length)} de {filteredBrokers.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPageBrokers}
                          disabled={currentPageBrokers === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPagesBrokers) }, (_, i) => {
                            let pageNumber: number
                            if (totalPagesBrokers <= 5) {
                              pageNumber = i + 1
                            } else if (currentPageBrokers <= 3) {
                              pageNumber = i + 1
                            } else if (currentPageBrokers >= totalPagesBrokers - 2) {
                              pageNumber = totalPagesBrokers - 4 + i
                            } else {
                              pageNumber = currentPageBrokers - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChangeBrokers(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPageBrokers === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={handleNextPageBrokers}
                          disabled={currentPageBrokers === totalPagesBrokers}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {activeTab === 'parametros' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Configurações do Dashboard Corretor. Edite o valor conforme necessário.
                    </Text>
                  </div>

                  {loadingParameters ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                            <th className="px-4 py-2" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {parameters.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum parâmetro cadastrado.
                              </td>
                            </tr>
                          ) : (
                            parameters.map((param) => (
                              <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td
                                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md truncate"
                                  title={param.description ?? undefined}
                                >
                                  {param.description ?? '—'}
                                </td>
                                <td
                                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                                  title={String(param.value).length > 20 ? String(param.value) : undefined}
                                >
                                  {String(param.value).length > 20
                                    ? `${String(param.value).slice(0, 20)}…`
                                    : param.value}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <PermissionGuard moduleName="portais_dashboard_corretor" permissionType="update" fallback={null}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setEditingParameter(param)
                                        setParameterEditValue(param.value)
                                        const hasHtml = typeof param.value === 'string' && param.value.includes('<')
                                        setParameterEditMode(hasHtml ? 'html' : 'simple')
                                        setIsParameterModalOpen(true)
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  </PermissionGuard>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal de Edição de Parâmetro */}
            {isParameterModalOpen && editingParameter && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                  <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={() => {
                      setIsParameterModalOpen(false)
                      setEditingParameter(null)
                      setParameterEditMode('simple')
                    }}
                  />
                  <div
                    className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 pt-5 pb-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Editar parâmetro
                        </Heading>
                        <button
                          type="button"
                          onClick={() => {
                            setIsParameterModalOpen(false)
                            setEditingParameter(null)
                            setParameterEditMode('simple')
                          }}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                          <span className="sr-only">Fechar</span>
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                        {editingParameter.key}
                      </Text>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 block">
                              Valor *
                            </Text>
                            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 overflow-hidden text-xs">
                              <button
                                type="button"
                                className={`px-2 py-1 ${parameterEditMode === 'simple' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('simple')}
                              >
                                Valor simples
                              </button>
                              <button
                                type="button"
                                className={`px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${parameterEditMode === 'html' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('html')}
                              >
                                Descritivo HTML
                              </button>
                            </div>
                          </div>
                          {parameterEditMode === 'simple' ? (
                            <Input
                              type="text"
                              value={parameterEditValue}
                              onChange={(e) => setParameterEditValue(e.target.value)}
                              className="w-full"
                            />
                          ) : (
                            <div className="w-full text-sm text-gray-600 dark:text-gray-400">
                              <Editor
                                value={parameterEditValue}
                                onChange={(value) => setParameterEditValue(value)}
                                placeholder="Digite o conteúdo em HTML (parágrafos, listas, etc.)"
                                stickyToolbar
                              />
                            </div>
                          )}
                          <Text size="xs" className="mt-1 text-gray-500 dark:text-gray-400">
                            Use \"Valor simples\" para textos ou números diretos, ou \"Descritivo HTML\" para conteúdo formatado com marcadores HTML.
                          </Text>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Descrição
                          </Text>
                          <div className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                            {editingParameter.description ?? '—'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsParameterModalOpen(false)
                            setEditingParameter(null)
                          }}
                        >
                          Cancelar
                        </Button>
                        <PermissionGuard moduleName="portais_dashboard_corretor" permissionType="update" fallback={null}>
                          <Button
                            variant="solid"
                            disabled={savingParameter || !parameterEditValue.trim()}
                            onClick={async () => {
                              if (!editingParameter?.id || !parameterEditValue.trim()) return
                              setSavingParameter(true)
                              const oldValue = editingParameter.value
                              const newValue = parameterEditValue.trim()
                              try {
                                await dashcorretorService.updateParameter(editingParameter.id, {
                                  value: newValue,
                                })
                                await userLogsService.logUpdate(
                                  'parameter',
                                  editingParameter.id,
                                  `Alterou parâmetro no Dashboard Corretor: ${editingParameter.key}`,
                                  { value: oldValue },
                                  { value: newValue },
                                  { parameter_key: editingParameter.key, context: 'dashboard_corretor' }
                                )
                                await loadParameters()
                                setIsParameterModalOpen(false)
                                setEditingParameter(null)
                              } catch (err: any) {
                                console.error('Erro ao salvar parâmetro do dashboard corretor:', err)
                                alert(err?.message || 'Erro ao salvar parâmetro. Tente novamente.')
                              } finally {
                                setSavingParameter(false)
                              }
                            }}
                          >
                            {savingParameter ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </PermissionGuard>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
      </PermissionGuard>
    </div>
  )
}

export default DashboardCorretor
